import { DB_ONLY_CLICK_ID_KEYS, RAW_UTM_KEYS } from './mixpanelTypes'
import {
  MIXPANEL_ATTRIBUTION_CONTEXT_STORAGE_KEY,
  MIXPANEL_LAST_NON_DIRECT_STORAGE_KEY,
  safeReadJson,
  safeWriteJson
} from './mixpanelStorage'
import type {
  AttributionContext,
  DbOnlyClickIds,
  DeviceCategory,
  MixpanelAdditionalInfo,
  MixpanelProperties,
  RawUtmProperties
} from './mixpanelTypes'
import type { MixpanelStorage } from './mixpanelStorage'

const LAST_NON_DIRECT_TTL_MS = 30 * 24 * 60 * 60 * 1000
const DEFAULT_SESSION_SOURCE = '(direct)'
const DEFAULT_SESSION_MEDIUM = '(none)'

interface LastNonDirect {
  sessionSource: string
  sessionMedium: string
  updatedAt: number
}

interface AttributionResolution {
  sessionSource: string
  sessionMedium: string
  rawUtm: RawUtmProperties
  dbOnlyClickIds: DbOnlyClickIds
  isNewNonDirect: boolean
}

interface GetAttributionContextOptions {
  url: URL
  referrer?: string
  currentOrigin?: string
  sessionStorage?: MixpanelStorage | null
  localStorage?: MixpanelStorage | null
  deviceCategory?: DeviceCategory
  createContextId?: () => string
  now?: number
}

let memoryContext: AttributionContext | null = null
let memoryStorage: MixpanelStorage | null | undefined

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isAllowlistedStringRecord(
  value: unknown,
  allowedKeys: readonly string[]
): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.keys(value).every((key) => allowedKeys.includes(key)) &&
    Object.values(value).every((item) => typeof item === 'string')
  )
}

function isAttributionContext(value: unknown): value is AttributionContext {
  if (!isRecord(value)) return false
  return (
    typeof value.contextId === 'string' &&
    typeof value.sessionSource === 'string' &&
    typeof value.sessionMedium === 'string' &&
    isAllowlistedStringRecord(value.rawUtm, RAW_UTM_KEYS) &&
    isAllowlistedStringRecord(value.dbOnlyClickIds, DB_ONLY_CLICK_ID_KEYS) &&
    (value.deviceCategory === 'desktop' ||
      value.deviceCategory === 'mobile' ||
      value.deviceCategory === 'tablet') &&
    typeof value.location === 'string'
  )
}

function isLastNonDirect(value: unknown): value is LastNonDirect {
  return (
    isRecord(value) &&
    typeof value.sessionSource === 'string' &&
    typeof value.sessionMedium === 'string' &&
    typeof value.updatedAt === 'number'
  )
}

function getBrowserStorage(
  key: 'sessionStorage' | 'localStorage'
): MixpanelStorage | null {
  if (typeof window === 'undefined') return null
  try {
    return window[key]
  } catch {
    return null
  }
}

function createContextId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function getDeviceCategory(): DeviceCategory {
  if (typeof navigator === 'undefined') return 'desktop'
  const userAgent = navigator.userAgent
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet'
  if (
    /mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(
      userAgent
    )
  ) {
    return 'mobile'
  }
  return 'desktop'
}

function normalizeSearchParam(value: string | null): string | undefined {
  return value?.trim() || undefined
}

function pickRawUtm(searchParams: URLSearchParams): RawUtmProperties {
  const rawUtm: RawUtmProperties = {}
  for (const key of RAW_UTM_KEYS) {
    const value = normalizeSearchParam(searchParams.get(key))
    if (value) rawUtm[key] = value
  }
  return rawUtm
}

function pickDbOnlyClickIds(searchParams: URLSearchParams): DbOnlyClickIds {
  const clickIds: DbOnlyClickIds = {}
  for (const key of DB_ONLY_CLICK_ID_KEYS) {
    const value = normalizeSearchParam(searchParams.get(key))
    if (value) clickIds[key] = value
  }
  return clickIds
}

function getReferrerHostname(referrer: string): string | undefined {
  try {
    return new URL(referrer).hostname.replace(/^www\./, '')
  } catch {
    return undefined
  }
}

function isInternalReferrer(hostname: string, currentOrigin?: string): boolean {
  if (hostname.includes('nordy.ai')) return true
  if (!currentOrigin) return false
  try {
    return hostname === new URL(currentOrigin).hostname.replace(/^www\./, '')
  } catch {
    return false
  }
}

function resolveReferrer(hostname: string) {
  const organicSources: Array<[string, string]> = [
    ['search.naver.com', 'naver'],
    ['m.search.naver.com', 'naver'],
    ['search.daum', 'daum'],
    ['bing.com', 'bing'],
    ['yahoo', 'yahoo'],
    ['yandex', 'yandex'],
    ['duckduckgo', 'duckduckgo'],
    ['baidu', 'baidu'],
    ['ecosia.org', 'ecosia'],
    ['qwant.com', 'qwant'],
    ['search.brave.com', 'brave'],
    ['aol.com', 'aol']
  ]
  const referralSources: Array<[string, string]> = [
    ['chatgpt.com', 'chatgpt'],
    ['perplexity', 'perplexity'],
    ['gemini.google.com', 'gemini'],
    ['copilot.com', 'copilot'],
    ['grok.com', 'grok'],
    ['claude.ai', 'claude'],
    ['manus.im', 'manus']
  ]
  if (
    hostname.includes('google') &&
    !hostname.includes('mail') &&
    !hostname.includes('docs') &&
    !hostname.includes('classroom') &&
    !hostname.includes('keep') &&
    !hostname.includes('ads') &&
    !hostname.includes('accounts') &&
    !hostname.includes('translate') &&
    !hostname.includes('gemini')
  ) {
    return { sessionSource: 'google', sessionMedium: 'organic' }
  }
  for (const [pattern, source] of organicSources) {
    if (hostname.includes(pattern)) {
      return { sessionSource: source, sessionMedium: 'organic' }
    }
  }
  for (const [pattern, source] of referralSources) {
    if (hostname.includes(pattern)) {
      return { sessionSource: source, sessionMedium: 'referral' }
    }
  }
  return { sessionSource: hostname, sessionMedium: 'referral' }
}

function readLastNonDirect(
  storage: MixpanelStorage | null,
  now: number
): LastNonDirect | null {
  const value = safeReadJson(
    storage,
    MIXPANEL_LAST_NON_DIRECT_STORAGE_KEY,
    isLastNonDirect
  )
  if (!value || now - value.updatedAt >= LAST_NON_DIRECT_TTL_MS) return null
  return value
}

function resolveAttribution({
  url,
  referrer = '',
  currentOrigin,
  lastNonDirect
}: {
  url: URL
  referrer?: string
  currentOrigin?: string
  lastNonDirect: LastNonDirect | null
}): AttributionResolution {
  const rawUtm = pickRawUtm(url.searchParams)
  const dbOnlyClickIds = pickDbOnlyClickIds(url.searchParams)
  if (rawUtm.utm_source) {
    return {
      sessionSource: rawUtm.utm_source,
      sessionMedium: rawUtm.utm_medium ?? '(not set)',
      rawUtm,
      dbOnlyClickIds,
      isNewNonDirect: true
    }
  }
  const inferred = (() => {
    if (dbOnlyClickIds.gclid || dbOnlyClickIds.gad_source) {
      return { sessionSource: 'google', sessionMedium: 'cpc' }
    }
    if (dbOnlyClickIds.naver_clid) {
      return { sessionSource: 'naver', sessionMedium: 'cpc' }
    }
    if (dbOnlyClickIds.kakao_clid) {
      return { sessionSource: 'kakao', sessionMedium: 'cpc' }
    }
    if (dbOnlyClickIds.fbclid) {
      return { sessionSource: 'facebook', sessionMedium: 'paid_social' }
    }
    if (dbOnlyClickIds.ttclid) {
      return { sessionSource: 'tiktok', sessionMedium: 'paid_social' }
    }
    return null
  })()
  if (inferred) {
    return {
      ...inferred,
      rawUtm,
      dbOnlyClickIds,
      isNewNonDirect: true
    }
  }
  const hostname = getReferrerHostname(referrer)
  if (hostname && !isInternalReferrer(hostname, currentOrigin)) {
    return {
      ...resolveReferrer(hostname),
      rawUtm,
      dbOnlyClickIds,
      isNewNonDirect: true
    }
  }
  return {
    sessionSource: lastNonDirect?.sessionSource ?? DEFAULT_SESSION_SOURCE,
    sessionMedium: lastNonDirect?.sessionMedium ?? DEFAULT_SESSION_MEDIUM,
    rawUtm,
    dbOnlyClickIds,
    isNewNonDirect: false
  }
}

export function getOrCreateAttributionContext(
  options: GetAttributionContextOptions
): AttributionContext {
  const sessionStorage =
    options.sessionStorage ?? getBrowserStorage('sessionStorage')
  const savedContext = safeReadJson(
    sessionStorage,
    MIXPANEL_ATTRIBUTION_CONTEXT_STORAGE_KEY,
    isAttributionContext
  )
  if (savedContext) return savedContext
  if (memoryStorage === sessionStorage && memoryContext) return memoryContext

  const now = options.now ?? Date.now()
  const localStorage = options.localStorage ?? getBrowserStorage('localStorage')
  const resolution = resolveAttribution({
    url: options.url,
    referrer: options.referrer,
    currentOrigin: options.currentOrigin ?? options.url.origin,
    lastNonDirect: readLastNonDirect(localStorage, now)
  })
  const context: AttributionContext = {
    contextId: (options.createContextId ?? createContextId)(),
    sessionSource: resolution.sessionSource,
    sessionMedium: resolution.sessionMedium,
    rawUtm: resolution.rawUtm,
    dbOnlyClickIds: resolution.dbOnlyClickIds,
    deviceCategory: options.deviceCategory ?? getDeviceCategory(),
    location: options.url.pathname
  }
  if (resolution.isNewNonDirect) {
    safeWriteJson(localStorage, MIXPANEL_LAST_NON_DIRECT_STORAGE_KEY, {
      sessionSource: context.sessionSource,
      sessionMedium: context.sessionMedium,
      updatedAt: now
    } satisfies LastNonDirect)
  }
  if (
    !safeWriteJson(
      sessionStorage,
      MIXPANEL_ATTRIBUTION_CONTEXT_STORAGE_KEY,
      context
    )
  ) {
    memoryContext = context
    memoryStorage = sessionStorage
  } else {
    memoryContext = null
    memoryStorage = undefined
  }
  return context
}

export function toMixpanelAttributionProperties(
  context: AttributionContext
): MixpanelProperties {
  return {
    session_source: context.sessionSource,
    session_medium: context.sessionMedium,
    ...context.rawUtm
  }
}

export function toMixpanelAdditionalInfo(
  context: AttributionContext,
  location: string
): MixpanelAdditionalInfo {
  return {
    context_id: context.contextId,
    session_source: context.sessionSource,
    session_medium: context.sessionMedium,
    ...context.rawUtm,
    ...context.dbOnlyClickIds,
    device_category: context.deviceCategory,
    location
  }
}
